import { CursorPageSchema, CursorPaginatedResponseSchema } from 'notable-common';
import { z } from 'zod';
import { NotificationContentModality, NotificationContentSchema } from './content.js';
import { NotificationTimingSchema } from './timing.js';
/**
 * A named, prioritized set of notification configs. The scheme says *what* is sent and *when*, by
 * pairing each timing of a schedule with the content to send at it. Where two schemes could apply,
 * the higher priority wins.
 */
export const NotificationSchemeSchema = z.object({
    id: z.uuid(),
    created: z.iso.datetime(),
    updated: z.iso.datetime(),
    name: z.string(),
    organizationId: z.uuid().nullable(),
    priority: z.number(),
    deletedTimestamp: z.iso.datetime().nullable(),
});
/** One pairing inside a scheme: send this content at this timing. */
export const NotificationSchemeConfigSchema = z.object({
    id: z.uuid(),
    created: z.iso.datetime(),
    updated: z.iso.datetime(),
    notificationSchemeId: z.uuid(),
    notificationTimingId: z.uuid(),
    notificationContentId: z.uuid(),
    deletedTimestamp: z.null(),
    timing: NotificationTimingSchema,
    content: NotificationContentSchema,
});
/** A scheme with every config it sends, each carrying its own timing and content. */
export const NotificationSchemeFullSchema = NotificationSchemeSchema.extend({
    configs: z.array(NotificationSchemeConfigSchema),
});
/**
 * A scheme summarized for a list: the channels it sends on and the previsit form templates that
 * select it, keyed by template id.
 *
 * Both are aggregated by outer join, so a scheme with no config at all reports a single null
 * channel, and one no template points at reports no templates rather than an empty object.
 */
export const NotificationSchemeListEntrySchema = NotificationSchemeSchema.extend({
    kinds: z.array(z.enum(NotificationContentModality).nullable()),
    previsitFormTemplates: z.record(z.uuid(), z.string()).nullable(),
});
export const NotificationSchemeParamsSchema = z.object({
    schemeId: z.uuid(),
});
/**
 * The filters and page the list route accepts.
 *
 * No `sort`: the handler has no sortable columns, so results come back ordered by name.
 */
export const NotificationSchemeListQuerySchema = z
    .object({
    q: z.string().min(1).optional().describe('Return only schemes whose name contains this text.'),
    /*
     * An enum converted after validation rather than `z.stringbool`, whose input side is any
     * string: a caller reading the published schema would be told it may send one.
     */
    includeDeleted: z
        .enum(['true', 'false'])
        .transform((value) => value === 'true')
        .optional()
        .describe('Include schemes that have been deleted.'),
    // An empty array would serialize to no parameter at all, and so ask for every organization.
    organizationIds: z
        .union([z.uuid(), z.array(z.uuid()).min(1)])
        .optional()
        .describe('Return only schemes owned by these organizations.'),
    page: CursorPageSchema.optional()
        .default({ size: 25 })
        .describe('Page size, up to 500, and the cursor returned as meta.page.after by the previous page. Defaults to the first 25.'),
})
    .strict();
/** One page of schemes ordered by name, in the envelope every cursor-paginated route returns. */
export const NotificationSchemeListResponseSchema = CursorPaginatedResponseSchema(NotificationSchemeListEntrySchema);
//# sourceMappingURL=scheme.js.map